import { NextRequest, NextResponse } from 'next/server'
import { eq, count, and } from 'drizzle-orm'
import { auth } from '@/auth'
import { db } from '@/db'
import { tickets, ticketMessages, tenantConfigs, auditLog, projects } from '@/db/schema'
import { inngest } from '@/lib/inngest'
import { formatReferenceNumber } from '@/lib/ticket-reference'
import { checkRateLimit } from '@/lib/rate-limiter'

// 20 ticket submissions per user per hour
const TICKET_RATE_LIMIT = { limit: 20, windowSeconds: 60 * 60 }

const VALID_USER_PRIORITIES = ['critical', 'high', 'medium', 'low'] as const
type UserPriority = typeof VALID_USER_PRIORITIES[number]

function sanitize(text: string): string {
  return text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

async function generateReferenceNumber(
  tenantId: string,
  issueCategory: string,
  categoryAbbreviations: Record<string, string>,
): Promise<string> {
  const [{ value }] = await db
    .select({ value: count() })
    .from(tickets)
    .where(eq(tickets.tenantId, tenantId))
  return formatReferenceNumber(Number(value) + 1, issueCategory, categoryAbbreviations)
}

type StructuredFields = {
  projectId: string
  department: string
  dateOfOccurrence: string
  issueCategory: string
  affectedSystem: string
  userPriority: UserPriority
  submissionText: string
  successCriteria: string
  actionRequested: string
}

function validateStructuredFields(body: Record<string, unknown>): StructuredFields | string {
  const required: (keyof StructuredFields)[] = [
    'projectId',
    'department',
    'dateOfOccurrence',
    'issueCategory',
    'affectedSystem',
    'userPriority',
    'submissionText',
    'successCriteria',
    'actionRequested',
  ]

  const missing = required.filter((k) => !body[k] || typeof body[k] !== 'string' || !(body[k] as string).trim())
  if (missing.length > 0) {
    return `Missing required fields: ${missing.join(', ')}`
  }

  const priority = (body.userPriority as string).toLowerCase()
  if (!VALID_USER_PRIORITIES.includes(priority as UserPriority)) {
    return `userPriority must be one of: ${VALID_USER_PRIORITIES.join(', ')}`
  }

  const dateVal = body.dateOfOccurrence as string
  const today = new Date().toISOString().split('T')[0]
  if (dateVal > today) {
    return 'dateOfOccurrence cannot be in the future'
  }

  return {
    projectId: body.projectId as string,
    department: body.department as string,
    dateOfOccurrence: body.dateOfOccurrence as string,
    issueCategory: body.issueCategory as string,
    affectedSystem: body.affectedSystem as string,
    userPriority: priority as UserPriority,
    submissionText: body.submissionText as string,
    successCriteria: body.successCriteria as string,
    actionRequested: body.actionRequested as string,
  }
}

export async function POST(request: NextRequest) {
  const session = await auth()
  if (!session?.user?.id) {
    return NextResponse.json({ error: 'Unauthorized' }, { status: 401 })
  }

  const rl = await checkRateLimit(
    `tickets:user:${session.user.id}`,
    TICKET_RATE_LIMIT.limit,
    TICKET_RATE_LIMIT.windowSeconds,
  )
  if (!rl.success) {
    return NextResponse.json(
      { error: 'You have submitted too many tickets. Please wait before trying again.' },
      {
        status: 429,
        headers: { 'Retry-After': String(Math.ceil((rl.reset - Date.now()) / 1000)) },
      },
    )
  }

  const body = await request.json().catch(() => null)
  if (!body || typeof body !== 'object') {
    return NextResponse.json({ error: 'Invalid request body' }, { status: 400 })
  }

  const fieldsOrError = validateStructuredFields(body as Record<string, unknown>)
  if (typeof fieldsOrError === 'string') {
    return NextResponse.json({ error: fieldsOrError }, { status: 422 })
  }

  const fields = fieldsOrError
  const { idempotencyKey } = body as { idempotencyKey?: string }

  const tenantId = session.user.tenantId
  if (!tenantId) {
    return NextResponse.json({ error: 'User has no tenant association' }, { status: 403 })
  }

  // Validate project belongs to the same tenant and is active
  const [project] = await db
    .select({ id: projects.id })
    .from(projects)
    .where(
      and(
        eq(projects.id, fields.projectId),
        eq(projects.tenantId, tenantId),
        eq(projects.isActive, true),
      ),
    )
    .limit(1)

  if (!project) {
    return NextResponse.json({ error: 'Invalid or inactive project' }, { status: 422 })
  }

  // Validate against tenant char limit and read category abbreviations
  const [config] = await db
    .select({
      submissionMaxChars: tenantConfigs.submissionMaxChars,
      categoryAbbreviations: tenantConfigs.categoryAbbreviations,
    })
    .from(tenantConfigs)
    .where(eq(tenantConfigs.tenantId, tenantId))
    .limit(1)

  const maxChars = config?.submissionMaxChars ?? 4000
  if (fields.submissionText.length > maxChars) {
    return NextResponse.json(
      { error: `Submission exceeds maximum length of ${maxChars} characters` },
      { status: 422 },
    )
  }

  const referenceNumber = await generateReferenceNumber(
    tenantId,
    fields.issueCategory,
    (config?.categoryAbbreviations as Record<string, string>) ?? {},
  )
  const bodySanitized = sanitize(fields.submissionText)

  const [ticket] = await db
    .insert(tickets)
    .values({
      tenantId,
      submitterId: session.user.id,
      referenceNumber,
      status: 'SUBMITTED',
      aiProcessingState: 'queued',
      channel: 'web',
      projectId: fields.projectId,
      department: fields.department,
      dateOfOccurrence: fields.dateOfOccurrence,
      issueCategory: fields.issueCategory,
      affectedSystem: fields.affectedSystem,
      userPriority: fields.userPriority,
      successCriteria: fields.successCriteria,
      actionRequested: fields.actionRequested,
    })
    .returning()

  const [message] = await db
    .insert(ticketMessages)
    .values({
      tenantId,
      ticketId: ticket.id,
      authorId: session.user.id,
      role: 'line_manager',
      bodyRaw: fields.submissionText,
      bodySanitized,
      idempotencyKey: idempotencyKey ?? null,
    })
    .returning()

  await db
    .update(tickets)
    .set({ lastMessageId: message.id, updatedAt: new Date() })
    .where(eq(tickets.id, ticket.id))

  await db.insert(auditLog).values({
    tenantId,
    actorId: session.user.id,
    actorType: 'user',
    action: 'ticket.created',
    entityType: 'ticket',
    entityId: ticket.id,
    payload: { referenceNumber, status: 'SUBMITTED', issueCategory: fields.issueCategory },
  })

  try {
    await inngest.send({
      name: 'ticket/submitted',
      data: {
        ticketId: ticket.id,
        tenantId,
        messageId: message.id,
        ticketVersion: ticket.version,
      },
    })
  } catch {
    // Inngest unavailable — ticket created; AI processing will not fire until re-triggered
  }

  return NextResponse.json({ ticketId: ticket.id, referenceNumber }, { status: 201 })
}
